import React, { useState, useEffect, useRef } from 'react';
import DataTable from './DataTable';
import { getFeedbacks } from '../../lib/adminApi';
import { Star, Trophy, Clock, XCircle } from 'lucide-react';
import { formatToIST } from '../../lib/dateUtils';

function AdminFeedbacks() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  const hasFetched = useRef(false);

  const fetchFeedbacks = async (page = 1) => {
    setLoading(true);
    try {
      const data = await getFeedbacks(page, pagination.limit);
      setFeedbacks(data.feedbacks || []);
      setPagination({ page: data.page || 1, limit: data.limit || 20, total: data.total || 0 });
    } catch (err) {
      console.error('Failed to fetch feedbacks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchFeedbacks();
  }, []);

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  const columns = [
    { key: 'userEmail', label: 'User Email' },
    {
      key: 'feedbackType',
      label: 'Type',
      render: (val) => {
        const getTypeStyle = () => {
          if (val === 'VICTORY') return { bg: 'bg-green-600/20 text-green-400', icon: <Trophy className="w-3 h-3" />, label: 'Victory' };
          if (val === 'FAILED') return { bg: 'bg-red-600/20 text-red-400', icon: <XCircle className="w-3 h-3" />, label: 'Failed' };
          return { bg: 'bg-orange-600/20 text-orange-400', icon: <Clock className="w-3 h-3" />, label: 'Time Up' };
        };
        const style = getTypeStyle();
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 w-fit ${style.bg}`}>
            {style.icon}
            {style.label}
          </span>
        );
      },
    },
    {
      key: 'rating',
      label: 'Rating',
      render: (val) => renderStars(val),
    },
    {
      key: 'dinosaursCaptured',
      label: 'Progress',
      render: (val) => (
        <span className="text-amber-400 font-medium">{val || 0}/7</span>
      ),
    },
    {
      key: 'submittedAt',
      label: 'Submitted At',
      render: (val) => formatToIST(val),
    },
  ];

  const [expandedRow, setExpandedRow] = useState(null);

  const renderExpandedContent = (row) => {
    if (expandedRow !== row.rowId) return null;

    return (
      <div className="bg-gray-900 p-4 border-t border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <h4 className="text-sm font-medium text-gray-400 mb-2">Game Experience</h4>
            <p className="text-gray-200 text-sm bg-gray-800 p-3 rounded-lg min-h-[80px]">
              {row.gameExperience || <span className="text-gray-500 italic">No response</span>}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-400 mb-2">Deluge Learning</h4>
            <p className="text-gray-200 text-sm bg-gray-800 p-3 rounded-lg min-h-[80px]">
              {row.delugeLearning || <span className="text-gray-500 italic">No response</span>}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-400 mb-2">Suggestions</h4>
            <p className="text-gray-200 text-sm bg-gray-800 p-3 rounded-lg min-h-[80px]">
              {row.suggestions || <span className="text-gray-500 italic">No response</span>}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const actions = (row) => (
    <button
      onClick={() => setExpandedRow(expandedRow === row.rowId ? null : row.rowId)}
      className="px-3 py-1 bg-gray-700 text-gray-300 rounded text-sm hover:bg-gray-600"
    >
      {expandedRow === row.rowId ? 'Hide Details' : 'View Details'}
    </button>
  );

  // Calculate average rating
  const avgRating = feedbacks.length > 0
    ? (feedbacks.reduce((sum, f) => sum + (f.rating || 0), 0) / feedbacks.length).toFixed(1)
    : 0;

  const victoryCount = feedbacks.filter(f => f.feedbackType === 'VICTORY').length;
  const failedCount = feedbacks.filter(f => f.feedbackType === 'FAILED').length;
  const timeUpCount = feedbacks.filter(f => f.feedbackType === 'TIME_UP').length;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">User Feedback</h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">Total Feedback</p>
          <p className="text-2xl font-bold text-white">{pagination.total}</p>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">Average Rating</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold text-yellow-400">{avgRating}</p>
            <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">Victory</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold text-green-400">{victoryCount}</p>
            <Trophy className="w-5 h-5 text-green-400" />
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">Failed</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold text-red-400">{failedCount}</p>
            <XCircle className="w-5 h-5 text-red-400" />
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">Time Up</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold text-orange-400">{timeUpCount}</p>
            <Clock className="w-5 h-5 text-orange-400" />
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={feedbacks}
        loading={loading}
        pagination={pagination}
        onPageChange={fetchFeedbacks}
        actions={actions}
        emptyMessage="No feedback submitted yet."
        idKey="rowId"
        renderExpandedRow={renderExpandedContent}
      />
    </div>
  );
}

export default AdminFeedbacks;
